import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InstanceUpdatesDto } from '@cpm/shared';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { api } from '../api/client.js';
import { HelpLink } from './help.js';
import { Badge, Button, Card, CardHeader, ErrorNote, Input, Label, Select, Spinner } from './ui.js';

const QUERY_KEY = ['updates', 'status'] as const;

export function InstanceUpdatesPanel() {
  const queryClient = useQueryClient();
  const [windowStart, setWindowStart] = useState<string | null>(null);
  const [windowEnd, setWindowEnd] = useState<string | null>(null);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const status = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<InstanceUpdatesDto>('/api/updates/status'),
    refetchInterval: 30_000,
  });
  const saveSettings = useMutation({
    mutationFn: (input: { hostServerUuid?: string | null; automaticCoolifyUpdates?: boolean;
      automaticHostReboot?: boolean; preUpdateBackup?: boolean;
      maintenanceWindow?: { start: string; end: string } | null }) =>
      api.put('/api/updates/settings', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
  const hostAutomatic = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch<InstanceUpdatesDto>('/api/updates/host-automatic', { enabled }),
    onSuccess: (data) => queryClient.setQueryData(QUERY_KEY, data),
  });
  const check = useMutation({
    mutationFn: () => api.post<InstanceUpdatesDto>('/api/updates/check'),
    onSuccess: (data) => queryClient.setQueryData(QUERY_KEY, data),
  });
  const apply = useMutation({
    mutationFn: () => api.post('/api/updates/coolify/apply'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
  const installPackages = useMutation({
    mutationFn: (packages: string[]) => api.post('/api/updates/host-packages/apply', { packages }),
    onSuccess: () => {
      setSelectedPackages([]);
      return queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const data = status.data;
  const host = data?.host;
  const coolify = data?.coolify;
  const busy = apply.isPending || coolify?.lastRun?.status === 'running';
  const packageBusy = installPackages.isPending || data?.packageRun?.status === 'running';
  const start = windowStart ?? coolify?.maintenanceWindow?.start ?? '02:00';
  const end = windowEnd ?? coolify?.maintenanceWindow?.end ?? '04:00';

  return (
    <div className="mt-4">
      <Card>
        <CardHeader
          title="Instance updates"
          description={<>Host packages and the Coolify control plane · <HelpLink topic="updates/overview">How updates work</HelpLink></>}
          actions={
            <Button variant="secondary" loading={check.isPending} onClick={() => check.mutate()}
              disabled={!data || !coolify?.currentVersion}>
              <RefreshCw className="size-4" /> Check for updates
            </Button>
          }
        />
        <div className="space-y-5 p-5">
          {status.isPending && <Spinner />}
          {status.error && <ErrorNote error={status.error} />}
          {(saveSettings.error ?? hostAutomatic.error ?? check.error ?? apply.error ?? installPackages.error) != null &&
            <ErrorNote error={saveSettings.error ?? hostAutomatic.error ?? check.error ?? apply.error ?? installPackages.error} />}

          {data && (
            <>
              <div className="space-y-2">
                <Label htmlFor="update-host">Coolify host</Label>
                <Select id="update-host" value={coolify?.hostServerUuid ?? ''}
                  disabled={saveSettings.isPending}
                  onChange={(event) => saveSettings.mutate({ hostServerUuid: event.target.value || null })}>
                  <option value="">Select the server running Coolify</option>
                  {data.servers.map((server) => (
                    <option key={server.uuid} value={server.uuid}>
                      {server.name} ({server.ip})
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Uses the server access key from Settings → Usage metrics. The host must accept root SSH.
                </p>
              </div>

              <section className="space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-medium">Operating system packages</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {host?.operatingSystem ?? 'Select a host to review packages.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {host?.automaticPackageUpdates != null && (
                      <Badge tone={host.automaticPackageUpdates ? 'green' : 'gray'}>
                        Automatic {host.automaticPackageUpdates ? 'on' : 'off'}
                      </Badge>
                    )}
                    {host?.rebootRequired && <Badge tone="yellow">Reboot required</Badge>}
                    {host?.automaticReboot && <Badge tone="red">Automatic reboot enabled on host</Badge>}
                    {coolify?.automaticHostReboot && host?.rebootTimerActive &&
                      <Badge tone="red">Scheduled reboot enabled</Badge>}
                    {host?.automaticPackageUpdates && host.updateTimerActive === false &&
                      <Badge tone="red">APT timer inactive</Badge>}
                    {coolify?.maintenanceWindow && host?.automaticPackageUpdates && host.panelManagedPolicy && !host.updateWindowActive &&
                      <Badge tone="red">Host window inactive</Badge>}
                    {coolify?.automaticHostReboot && host?.rebootTimerActive === false &&
                      <Badge tone="red">Reboot timer inactive</Badge>}
                    <Button variant="secondary" loading={hostAutomatic.isPending}
                      disabled={!host || Boolean(host.error) || host.automaticPackageUpdates == null}
                      onClick={() => hostAutomatic.mutate(!(host?.automaticPackageUpdates && host.panelManagedPolicy))}>
                      {host?.automaticPackageUpdates && host.panelManagedPolicy
                        ? 'Turn off automatic updates' : 'Enable stable Ubuntu updates'}
                    </Button>
                  </div>
                </div>
                {host?.error && <ErrorNote error={host.error} />}
                {!host?.error && host && (
                  <>
                    <div className="space-y-2 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={Boolean(coolify?.maintenanceWindow)}
                          disabled={saveSettings.isPending}
                          onChange={(event) => saveSettings.mutate({
                            maintenanceWindow: event.target.checked ? { start, end } : null,
                            ...(!event.target.checked ? { automaticHostReboot: false } : {}),
                          })} />
                        Restrict automatic updates to a daily maintenance window
                      </label>
                      {coolify?.maintenanceWindow && (
                        <div className="flex flex-wrap items-end gap-2">
                          <div><Label htmlFor="updates-window-start">Start</Label><Input id="updates-window-start" type="time" value={start}
                            onChange={(event) => setWindowStart(event.target.value)} /></div>
                          <div><Label htmlFor="updates-window-end">End</Label><Input id="updates-window-end" type="time" value={end}
                            onChange={(event) => setWindowEnd(event.target.value)} /></div>
                          <Button variant="secondary" loading={saveSettings.isPending}
                            disabled={start === coolify.maintenanceWindow.start && end === coolify.maintenanceWindow.end}
                            onClick={() => saveSettings.mutate({ maintenanceWindow: { start, end } })}>Save window</Button>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Times use the selected host&apos;s timezone{host.timeZone ? ` (${host.timeZone})` : ''}. Panel-managed Ubuntu and Coolify installs start in this window; a running install may finish later.
                      </p>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={Boolean(coolify?.automaticHostReboot)}
                          disabled={!coolify?.maintenanceWindow || !host.automaticPackageUpdates || saveSettings.isPending}
                          onChange={(event) => saveSettings.mutate({ automaticHostReboot: event.target.checked })} />
                        Reboot automatically when Ubuntu requires it
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        When enabled, the host checks 10 minutes before the window closes. It skips reboot if APT or a panel Coolify update is active.
                        {host.rebootRequired ? ' A reboot is currently pending.' : ''}
                        {!coolify?.maintenanceWindow ? ' Set a maintenance window to enable this option.' : ''}
                        {!host.automaticPackageUpdates ? ' Enable Ubuntu unattended updates first.' : ''}
                        {host.automaticPackageUpdates && !host.panelManagedPolicy
                          ? ' This host uses its own Ubuntu update policy; enable the panel policy if package installs must also stay inside this window.' : ''}
                      </p>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {host.packages.length} package update{host.packages.length === 1 ? '' : 's'} available from cached APT lists.
                      {host.checkedAt ? ` Checked ${new Date(host.checkedAt).toLocaleString()}.` : ''}
                    </p>
                    {host.packages.length > 0 && (
                      <div className="max-h-80 overflow-auto rounded-lg ring-1 ring-slate-200 dark:ring-slate-700">
                        <table className="w-full text-left text-sm">
                          <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                            <tr><th className="px-3 py-2">Select</th><th className="px-3 py-2">Package</th><th className="px-3 py-2">Installed</th><th className="px-3 py-2">Available</th><th className="px-3 py-2">Source</th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {host.packages.map((pkg) => (
                              <tr key={pkg.name}>
                                <td className="px-3 py-2"><input type="checkbox" aria-label={`Select ${pkg.name}`}
                                  checked={selectedPackages.includes(pkg.name)}
                                  disabled={packageBusy || (selectedPackages.length >= 100 && !selectedPackages.includes(pkg.name))}
                                  onChange={(event) => setSelectedPackages((current) => event.target.checked
                                    ? [...current, pkg.name] : current.filter((name) => name !== pkg.name))} /></td>
                                <td className="px-3 py-2 font-medium">{pkg.name}</td>
                                <td className="px-3 py-2">{pkg.installed}</td>
                                <td className="px-3 py-2">{pkg.candidate}</td>
                                <td className="px-3 py-2">{pkg.source}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {host.packages.length > 0 && (
                      <div className="flex flex-wrap items-center gap-3">
                        <Button loading={installPackages.isPending} disabled={selectedPackages.length === 0 || packageBusy}
                          onClick={() => installPackages.mutate(selectedPackages)}>
                          Update selected packages{selectedPackages.length ? ` (${selectedPackages.length})` : ''}
                        </Button>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Installs up to 100 selected upgrades now; APT may also update required dependencies and restart services.
                        </p>
                      </div>
                    )}
                    {data.packageRun && (
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Selected package run: {data.packageRun.status} · {data.packageRun.packages.join(', ')}
                        {data.packageRun.error ? ` · ${data.packageRun.error}` : ''}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {host.automaticPackageUpdates && host.panelManagedPolicy
                        ? 'The panel policy includes stable Ubuntu updates and the host’s existing allowed origins.'
                        : host.automaticPackageUpdates
                          ? 'This host still uses its own unattended-upgrades policy. Enable the panel policy to include stable Ubuntu updates.'
                          : 'Automatic package installation is off. Pending updates remain visible.'}
                      {' '}Other repositories may need manual maintenance.
                    </p>
                  </>
                )}
              </section>

              <section className="space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-medium">Coolify</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Running {coolify?.currentVersion ?? 'unknown'} · Offered update {coolify?.latestVersion ?? 'unknown'}
                    </p>
                    {coolify?.releaseUrl && (
                      <a href={coolify.releaseUrl} target="_blank" rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline dark:text-brand-400">
                        Release notes <ExternalLink className="size-3" />
                      </a>
                    )}
                  </div>
                  <Badge tone={coolify?.updateAvailable ? 'yellow' : coolify?.updateAvailable === false ? 'green' : 'gray'}>
                    {coolify?.updateAvailable ? 'Update available' : coolify?.updateAvailable === false ? 'Current' : 'Unknown'}
                  </Badge>
                </div>
                {coolify?.checkError && <ErrorNote error={coolify.checkError} />}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={coolify?.automaticUpdates ? 'green' : 'gray'}>
                    Panel automatic updates {coolify?.automaticUpdates ? 'on' : 'off'}
                  </Badge>
                  <Button variant="secondary" loading={saveSettings.isPending}
                    disabled={!coolify?.hostServerUuid}
                    onClick={() => saveSettings.mutate({ automaticCoolifyUpdates: !coolify?.automaticUpdates })}>
                    {coolify?.automaticUpdates ? 'Turn off automatic updates' : 'Turn on automatic updates'}
                  </Button>
                  <Button loading={apply.isPending}
                    disabled={!coolify?.hostServerUuid || !coolify?.updateAvailable || busy}
                    onClick={() => apply.mutate()}>
                    {busy ? 'Coolify update in progress' : 'Apply Coolify update'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  The panel checks every 15 minutes when enabled and starts updates inside the window if one is set. Coolify&apos;s own automatic update setting is separate; turn it off in Coolify to use one scheduler.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={Boolean(coolify?.preUpdateBackup)} disabled={saveSettings.isPending}
                    onChange={(event) => saveSettings.mutate({ preUpdateBackup: event.target.checked })} />
                  Create a local Coolify recovery backup before every panel update
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  The backup includes Coolify&apos;s database, encryption key and SSH keys in a root-only host folder. An update stops if backup creation fails. Keep an off-host copy for host recovery.
                </p>
                {coolify?.lastRun && (
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Last run: {coolify.lastRun.status} · {new Date(coolify.lastRun.startedAt).toLocaleString()}
                    {coolify.lastRun.targetVersion ? ` · target ${coolify.lastRun.targetVersion}` : ''}
                    {coolify.lastRun.error ? ` · ${coolify.lastRun.error}` : ''}
                  </p>
                )}
                {coolify?.lastRun?.installerSha256 && (
                  <p className="break-all font-mono text-xs text-slate-500 dark:text-slate-400">
                    Installer SHA-256: {coolify.lastRun.installerSha256}
                  </p>
                )}
                {coolify?.lastRun?.backupPath && (
                  <div className="space-y-1">
                    <p className="break-all font-mono text-xs text-slate-500 dark:text-slate-400">
                      Pre-update backup: {coolify.lastRun.backupPath}
                    </p>
                    <a href="/api/updates/coolify/backup/download"
                      className="inline-flex rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-700">
                      Download recovery backup (.tar.gz)
                    </a>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
